/**
 * Remediation quote from an inspection report: each open remark with its guessed fix, quantity
 * and hours (editable), the lines and totals priced from the same sources as the quote, then the
 * Excel file or an invoice. See docs/decisions/0016-remediation-roomscan-quote-acceptance.md.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Id, Project } from '@elec/domain';
import { localeTag, type Locale, type Translate } from '@elec/i18n';
import { loadPriceList, type Platform, type StoredPriceList } from '@elec/platform';
import type { CloudClient } from '@elec/sync';
import type * as Exporters from '@elec/export';
import { availableSources, loadServerPrices, ordered, preferredOf, useReferenceOf } from './priceSources.ts';
import { remarksFor } from './Inspections.tsx';
import { Dialog } from './ui.tsx';

type X = typeof Exporters;

export function RemediationDialog(props: {
  project: Project;
  inspectionId: Id;
  t: Translate;
  locale: Locale;
  platform: Platform;
  baseName: string;
  client?: CloudClient | null;
  load(): Promise<X>;
  save(bytes: Uint8Array, name: string, ext: string, mime: string): Promise<void>;
  onInvoice?(quote: Exporters.Quote): void;
  onClose(): void;
}) {
  const { t, project, locale, inspectionId } = props;
  const [x, setX] = useState<X | null>(null);
  const [own, setOwn] = useState<StoredPriceList | null>(null);
  const [server, setServer] = useState<Awaited<ReturnType<typeof loadServerPrices>>['data']>(null);
  const [choices, setChoices] = useState<Exporters.RemedyChoice[] | null>(null);
  const [vatPct, setVat] = useState(21);
  useEffect(() => {
    void props.load().then((ex) => {
      setX(ex);
      setChoices(ex.remedyChoices(project, inspectionId));
      void loadServerPrices(ex, project, props.client ?? null).then((r) => setServer(r.data));
    });
    void loadPriceList().then(setOwn);
  }, []);
  const sources = useMemo(
    () =>
      x
        ? ordered(
            availableSources(x, own, server, useReferenceOf(props.platform), t('quote.sourceReference')),
            preferredOf(props.platform),
          )
        : [],
    [x, own, server],
  );
  const quote = useMemo(
    () =>
      x && choices
        ? x.remediationQuote(
            project,
            inspectionId,
            choices,
            { sources: sources.map((s) => ({ id: s.id, entries: s.entries })), vatPct },
            locale,
          )
        : null,
    [x, choices, sources, vatPct, project, inspectionId, locale],
  );
  const remarks = useMemo(() => remarksFor(project, inspectionId), [project, inspectionId]);
  const money = (n: number) => n.toLocaleString(localeTag(locale), { style: 'currency', currency: 'EUR' });
  const num = (v: string, d: number) => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : d;
  };
  const set = (i: number, patch: Partial<Exporters.RemedyChoice>) =>
    setChoices((cs) => cs && cs.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const unpriced = quote?.lines.filter((l) => l.unitPrice === undefined).length ?? 0;
  const empty = !quote || quote.lines.length === 0;
  return (
    <Dialog
      title={t('remedy.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.close')}</button>
          {props.onInvoice && (
            <button
              disabled={empty}
              onClick={() => quote && props.onInvoice!(quote)}
              data-testid="remedy-invoice"
            >
              {t('quote.invoice')}
            </button>
          )}
          <button
            className="primary"
            disabled={empty}
            data-testid="remedy-xlsx"
            onClick={() =>
              quote &&
              void props.save(
                x!.quoteXlsx(project, quote, locale, { title: x!.remediationTitle(locale), bom: false }),
                `${props.baseName} - ${t('remedy.file')}.xlsx`,
                'xlsx',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              )
            }
          >
            {t('quote.xlsx')}
          </button>
        </>
      }
    >
      <p className="muted">{t('remedy.intro')}</p>
      {choices && choices.length === 0 && <p data-testid="remedy-none">{t('remedy.none')}</p>}
      {x && choices && choices.length > 0 && (
        <div className="quote-table" tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>{t('remedy.remark')}</th>
                <th>{t('remedy.fix')}</th>
                <th className="num">{t('remedy.qty')}</th>
                <th className="num">{t('remedy.hours')}</th>
              </tr>
            </thead>
            <tbody data-testid="remedy-rows">
              {choices.map((c, i) => {
                const r = project.remarks[c.remarkId]!;
                const n = remarks.findIndex((m) => m.id === r.id) + 1;
                return (
                  <tr key={c.remarkId} className={c.include ? '' : 'muted'} data-testid="remedy-row">
                    <td>
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={c.include}
                          aria-label={t('remedy.include', { n })}
                          onChange={(e) => set(i, { include: e.target.checked })}
                        />
                        <span>
                          <strong>
                            {n}.{r.article ? ` ${r.article}` : ''}
                          </strong>{' '}
                          {r.text}
                        </span>
                      </label>
                    </td>
                    <td>
                      <select
                        value={c.fix}
                        disabled={!c.include}
                        data-testid="remedy-fix"
                        onChange={(e) => {
                          const fix = e.target.value as Exporters.FixKind;
                          set(i, { fix, hours: (x.FIX_BY_KIND.get(fix)?.hours ?? 1) * c.qty });
                        }}
                      >
                        {x.FIXES.map((f) => (
                          <option key={f.kind} value={f.kind}>
                            {x.fixLabel(f.kind, locale)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        className="narrow"
                        inputMode="decimal"
                        disabled={!c.include}
                        value={c.qty}
                        data-testid="remedy-qty"
                        onChange={(e) => {
                          const qty = num(e.target.value, 1);
                          set(i, { qty, hours: (x.FIX_BY_KIND.get(c.fix)?.hours ?? 1) * qty });
                        }}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="narrow"
                        inputMode="decimal"
                        disabled={!c.include}
                        value={c.hours}
                        data-testid="remedy-hours"
                        onChange={(e) => set(i, { hours: num(e.target.value, 0) })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {quote && quote.lines.length > 0 && (
        <>
          <div className="form-grid remedy-vat">
            <div className="field">
              <label htmlFor="rm-vat">{t('quote.vat')}</label>
              <select id="rm-vat" value={vatPct} onChange={(e) => setVat(Number(e.target.value))}>
                <option value={21}>{t('quote.vat21')}</option>
                <option value={6}>{t('quote.vat6')}</option>
              </select>
            </div>
          </div>
          <div className="quote-table" tabIndex={0}>
            <table>
              <tbody data-testid="remedy-lines">
                {quote.lines.map((l, i) => (
                  <tr key={i} className={l.unitPrice === undefined ? 'unpriced' : ''}>
                    <td>{l.description}</td>
                    <td className="num">
                      {l.quantity} {l.unit}
                    </td>
                    <td className="num">{l.unitPrice === undefined ? '—' : money(l.unitPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            <strong data-testid="remedy-totals">
              {t('quote.totals', {
                sub: money(quote.subtotal),
                vat: money(quote.vat),
                total: money(quote.total),
              })}
            </strong>
          </p>
          {unpriced > 0 && <p className="muted">{t('remedy.unpriced', { n: unpriced })}</p>}
        </>
      )}
    </Dialog>
  );
}
